import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Trash2, Upload } from '@tamagui/lucide-icons'
import { useState, useRef } from 'react'
import { useAppStore } from '@/store'
import { customEmojis, users } from '@/mocks'
import { TIER_CONFIGS } from '@/mocks'
import { useServerStore } from '@/store'

const Container = styled(View, { flex: 1, flexDirection: 'column' })

const Header = styled(View, {
  paddingHorizontal: 32, paddingVertical: 20,
  borderBottomWidth: 1, borderBottomColor: '$borderDefault',
  flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
})

const Title = styled(Text, {
  fontFamily: '$fontHeading', fontWeight: '600', fontSize: 20, color: '$surfaceForeground',
})

const Body = styled(View, { padding: 32, gap: 16, maxWidth: 700 })

const UploadArea = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  padding: 16,
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
})

const UploadButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
  height: 32,
  paddingHorizontal: 12,
  borderRadius: '$borderRadiusMd',
  backgroundColor: '$accent',
  cursor: 'pointer',
  hoverStyle: { opacity: 0.9 },
})

const EmojiRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  paddingHorizontal: 12,
  paddingVertical: 10,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
  hoverStyle: { backgroundColor: '$sidebarHover' },
})

const DeleteButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: { backgroundColor: 'rgba(207, 62, 79, 0.15)' },
})

const EmojiTableHeader = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  paddingHorizontal: 12,
  paddingVertical: 8,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

export function ServerEmojiSettings() {
  const { activeServerId } = useAppStore()
  const server = useServerStore((s) => activeServerId ? s.servers[activeServerId] : undefined)
  const theme = useTheme()
  const [newEmojiName, setNewEmojiName] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const emojis = activeServerId ? customEmojis[activeServerId] || [] : []
  const maxEmoji = server ? TIER_CONFIGS[server.tier].customEmoji : 50

  return (
    <Container>
      <Header>
        <Title>Emoji</Title>
        <Text fontFamily="$fontBody" fontSize={13} color="$sidebarMuted">
          {emojis.length} / {maxEmoji} slots used
        </Text>
      </Header>
      <ScrollView flex={1}>
        <Body>
          <UploadArea>
            <UploadButton onPress={() => fileInputRef.current?.click()}>
              <Upload size={14} color="$surface" />
              <Text fontFamily="$fontBody" fontWeight="600" fontSize={13} color="$surface">Upload Emoji</Text>
            </UploadButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
            />
            <View flex={1} gap={4}>
              <input
                value={newEmojiName}
                onChange={(e) => setNewEmojiName(e.target.value)}
                placeholder="Emoji name (e.g. party_parrot)"
                style={{
                  background: 'transparent',
                  border: `1px solid ${theme.borderDefault?.val || '#2A3142'}`,
                  borderRadius: 4, padding: '6px 10px',
                  color: theme.surfaceForeground?.val || '#E2E8F0',
                  fontFamily: 'inherit', fontSize: 13,
                  outline: 'none', width: '100%', boxSizing: 'border-box',
                }}
              />
            </View>
          </UploadArea>

          <View borderRadius="$borderRadiusLg" borderWidth={1} borderColor="$borderDefault" overflow="hidden">
            <EmojiTableHeader>
              <View width={36} />
              <Text fontFamily="$fontBody" fontSize={11} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5} flex={1}>
                Name
              </Text>
              <Text fontFamily="$fontBody" fontSize={11} fontWeight="600" color="$color8" textTransform="uppercase" letterSpacing={0.5} width={100}>
                Uploaded By
              </Text>
              <View width={28} />
            </EmojiTableHeader>

            {emojis.map((emoji) => {
              const uploader = users[emoji.uploadedBy]
              return (
                <EmojiRow key={emoji.id}>
                  <View width={36} alignItems="center">
                    <img
                      src={emoji.imageUrl}
                      alt={emoji.name}
                      style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain' }}
                    />
                  </View>
                  <Text fontFamily="$fontMono" fontSize={13} color="$surfaceForeground" flex={1}>
                    :{emoji.name}:
                  </Text>
                  <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted" width={100} numberOfLines={1}>
                    {uploader?.displayName || 'Unknown'}
                  </Text>
                  <DeleteButton>
                    <Trash2 size={14} color="$error" />
                  </DeleteButton>
                </EmojiRow>
              )
            })}

            {emojis.length === 0 && (
              <View padding={32} alignItems="center">
                <Text fontFamily="$fontBody" fontSize={14} color="$sidebarMuted">
                  No custom emoji yet. Upload one to get started!
                </Text>
              </View>
            )}
          </View>
        </Body>
      </ScrollView>
    </Container>
  )
}
